import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  ChevronDownIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  LinkIcon,
  PaperAirplaneIcon,
  ShoppingCartIcon,
  UserPlusIcon,
} from '@heroicons/react/20/solid';
import { ClockIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { Link } from '@tanstack/react-router';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { EmptyState } from '../../../components/empty-state';
import { Loading } from '../../../components/loading';
import { SearchInput } from '../../../components/search-input';
import type {
  Event,
  OrderSentPayload,
  ReceivingCompletedPayload,
  StoreUploadEvent,
  VendorIntegrationPayload,
} from '../../../data/event';
import type { OnboardingOverviewStore } from '../../../data/onboarding';
import { isNightlyTrigger } from '../../../data/upload';
import type { Vendor, VendorIntegration } from '../../../data/vendor';
import { formatPercent, shortCurrencyFormatter } from '../../../helpers/format-currency';
import { formatAdminTimestamp, formatDate, formatDateISO } from '../../../helpers/format-date';
import { useOnboardingOverview, useOnboardingStoreEvents } from '../../../hooks/onboarding';
import { useStoreVendorIntegrations, useStoreVendors } from '../../../hooks/vendors';
import { onboardingTimelineRoute } from '../../../router';
import { TimelineHeatmap } from './timeline-heatmap';

// Day-N markers are only useful inside the onboarding window. After ~6 months
// the number becomes a giant noise digit (e.g. Day 2682 for a 7-year-old
// store) and obscures the timeline rather than illuminating it.
const DAY_N_VISIBLE_THROUGH_DAYS = 180;

interface GaugeCardProps {
  title: string;
  value: ReactNode;
  subline?: string;
  sparkline?: number[];
}

// Shared layout for "vital signs" gauges: title at top, big value, optional
// subline, optional 90d sparkline. Inline-flex so a row of cards naturally
// wraps on narrow screens.
function GaugeCard({ title, value, subline, sparkline }: GaugeCardProps) {
  return (
    <div className="inline-flex min-h-36 flex-col items-start rounded-sm border border-gray-200 bg-white px-4 py-3">
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className="mt-1 text-3xl font-semibold text-gray-900">{value}</p>
      {subline && <p className="mt-0.5 text-sm text-gray-500">{subline}</p>}
      {sparkline && sparkline.length > 0 && (
        <SparkLineChart
          data={sparkline}
          width={120}
          height={24}
          showTooltip={false}
          showHighlight={false}
          className="mt-auto pt-2"
        />
      )}
    </div>
  );
}

interface NegativeQohGaugeProps {
  uploads: StoreUploadEvent[];
}

// Reads negative_qoh_count from the most recent store_upload event for the
// current value and walks the entire window for the 90d sparkline. Omits the
// percent subline when there's no sales signal (fresh stores).
function NegativeQohGauge({ uploads }: NegativeQohGaugeProps) {
  const latest = uploads[0]; // events come back newest-first
  if (!latest) {
    return null;
  }

  const negative = latest.payload.negative_qoh_count;
  const sold365 = latest.payload.products_sold_365_count;
  const percent = sold365 > 0 ? formatPercent(negative / sold365) : null;
  const sparkline = uploads
    .filter((e) => e.payload.enabled_products_count > 0) // pre-feature events lack this field (default 0)
    .reverse() // events are newest-first; reverse for chronological sparkline
    .map((e) => e.payload.negative_qoh_count);

  return (
    <GaugeCard
      title="Negative QOH"
      value={negative.toLocaleString()}
      subline={percent !== null ? `${percent} of products sold last year` : undefined}
      sparkline={sparkline}
    />
  );
}

interface PricingCoverageGaugeProps {
  uploads: StoreUploadEvent[];
}

// Three-bucket pricing coverage: products with a real item_code in a priced
// vendor (primary), products recovered via barcode→canonical (suggested), and
// the rest (uncovered). Buckets are mutually exclusive — suggest_item_codes
// clears SuggestedVendorCode when a real ItemCode exists.
function PricingCoverageGauge({ uploads }: PricingCoverageGaugeProps) {
  const latest = uploads[0];
  if (!latest) {
    return null;
  }

  const { priced_product_count, priced_item_code_count, suggested_item_code_count } =
    latest.payload;
  const covered = priced_item_code_count + suggested_item_code_count;
  const pctStr = formatPercent(priced_product_count > 0 ? covered / priced_product_count : 0);

  const sparkline = uploads
    .filter((e) => e.payload.priced_product_count > 0) // pre-feature events lack this field (default 0)
    .reverse()
    .map((e) => {
      const c = e.payload.priced_item_code_count + e.payload.suggested_item_code_count;
      const d = e.payload.priced_product_count;

      return (c / d) * 100;
    });

  return (
    <GaugeCard
      title="Item Code Coverage"
      value={
        <>
          {priced_item_code_count.toLocaleString()}
          {suggested_item_code_count > 0 && (
            <>
              {' + '}
              <span className="rounded bg-amber-500 px-1 text-white">
                {suggested_item_code_count.toLocaleString()}
              </span>
            </>
          )}
        </>
      }
      subline={`${pctStr} of ${priced_product_count.toLocaleString()} priced-vendor products`}
      sparkline={sparkline}
    />
  );
}

interface VendorIntegrationGaugeProps {
  vendors: Vendor[];
  integrations: VendorIntegration[];
  state: string;
}

// Two numbers, both useful: how many enabled vendors actually have an
// integration wired up, and what the theoretical ceiling is for this store's
// state (number of integrations available in their state code). "8 / 17, of
// 8 possible" tells you both "where you are" and "is there room to grow".
function VendorIntegrationGauge({ vendors, integrations, state }: VendorIntegrationGaugeProps) {
  const enabledVendors = vendors.filter((v) => !v.disabled);
  const integrationByName = new Map(integrations.map((i) => [i.normalized_name, i]));
  const integrated = enabledVendors.filter((v) => integrationByName.has(v.normalized_name)).length;

  return (
    <GaugeCard
      title="Vendor integrations"
      value={`${integrated} / ${enabledVendors.length}`}
      subline={`of ${integrations.length} possible in ${state}`}
    />
  );
}

interface StorePickerProps {
  current: OnboardingOverviewStore;
  stores: OnboardingOverviewStore[];
}

function StorePicker({ current, stores }: StorePickerProps) {
  const sorted = useMemo(
    () => [...stores].sort((a, b) => a.store.name.localeCompare(b.store.name)),
    [stores]
  );

  return (
    <Popover className="relative inline-block">
      <PopoverButton className="inline-flex items-center gap-2 rounded-sm border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
        {current.store.name}
        <ChevronDownIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
      </PopoverButton>
      <PopoverPanel
        anchor="bottom start"
        className="z-popover mt-1 w-80 rounded-md bg-popover shadow-popover"
      >
        {({ close }) => <StorePickerList current={current} sorted={sorted} close={close} />}
      </PopoverPanel>
    </Popover>
  );
}

interface StorePickerListProps {
  current: OnboardingOverviewStore;
  sorted: OnboardingOverviewStore[];
  close: () => void;
}

// Mounted inside PopoverPanel so the search query resets each time the panel
// reopens (PopoverPanel unmounts its children when closed).
function StorePickerList({ current, sorted, close }: StorePickerListProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return sorted;
    }

    return sorted.filter((s) => s.store.name.toLowerCase().includes(needle));
  }, [sorted, query]);

  return (
    <>
      <div className="border-b border-gray-200 p-2">
        <SearchInput inputRef={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      {visible.length === 0 ? (
        <p className="p-3 text-sm text-gray-500">No stores match &ldquo;{query}&rdquo;.</p>
      ) : (
        <div role="listbox" aria-label="Stores" className="max-h-72 overflow-y-auto p-1">
          {visible.map((s) => {
            const isCurrent = s.store.id === current.store.id;

            return (
              <Link
                key={s.store.id}
                to="/admin/onboarding/$storeId"
                params={{ storeId: s.store.id }}
                onClick={() => close()}
                role="option"
                aria-selected={isCurrent}
                className={`block rounded-sm px-2 py-1.5 text-sm ${
                  isCurrent
                    ? 'bg-primary-50 font-medium text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {s.store.name}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

type EventIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface EventDisplay {
  Icon: EventIcon;
  bgClass: string;
  label: string;
  muted?: boolean;
}

// Surface the normalized distributor name first (Orderbot keys integrations off
// it); append the store's local rename in parens when it differs.
function resolveVendor(
  p: VendorIntegrationPayload | OrderSentPayload | ReceivingCompletedPayload,
  vendorNameById: Map<string, string>
): string {
  const normalized = p.vendor_normalized_name;
  const local = p.vendor_id ? vendorNameById.get(p.vendor_id) : undefined;

  if (!normalized && !local) {
    return p.vendor_id ?? 'Unknown vendor';
  }

  if (!normalized) {
    return local ?? p.vendor_id ?? 'Unknown vendor';
  }

  if (local && local.toLowerCase() !== normalized.toLowerCase()) {
    return `${normalized} (${local})`;
  }

  return normalized;
}

function storeUploadLabel(scheduled: boolean, count: number): string {
  if (scheduled) {
    return count > 0 ? `Nightly upload: ${count} products` : 'Nightly upload';
  }

  return count > 0 ? `User Uploaded ${count} products` : 'Uploaded store data';
}

function eventDisplay(event: Event, vendorNameById: Map<string, string>): EventDisplay {
  switch (event.event_type) {
    case 'signup':
      return { Icon: UserPlusIcon, bgClass: 'bg-green-500', label: 'Signed up' };
    case 'trial_converted':
      return {
        Icon: CurrencyDollarIcon,
        bgClass: 'bg-green-500',
        label: 'Trial converted to paid',
      };
    case 'vendor_integration':
      return {
        Icon: LinkIcon,
        bgClass: 'bg-blue-500',
        label: `Connected ${resolveVendor(event.payload, vendorNameById)}`,
      };
    case 'store_upload': {
      const scheduled = isNightlyTrigger(event.payload.trigger);
      const count = event.payload.product_count;

      return {
        Icon: ArrowUpTrayIcon,
        bgClass: scheduled ? 'bg-blue-300' : 'bg-blue-500',
        label: storeUploadLabel(scheduled, count),
        muted: scheduled,
      };
    }

    case 'order_sent':
      return {
        Icon: PaperAirplaneIcon,
        bgClass: 'bg-purple-500',
        label: `Sent ${resolveVendor(event.payload, vendorNameById)} an order: ${event.payload.item_count} items, ${shortCurrencyFormatter(event.payload.total_cents / 100)}`,
      };
    case 'order_edited':
      // Heatmap-only; filtered out before the list renders. Case present so
      // the exhaustive switch type-checks; never reached in practice.
      return {
        Icon: PaperAirplaneIcon,
        bgClass: 'bg-purple-300',
        label: `Edited ${event.payload.edit_count} item(s)`,
      };
    case 'receiving_completed': {
      const vendor = event.payload.vendor_id ? resolveVendor(event.payload, vendorNameById) : null;

      return {
        Icon: InboxArrowDownIcon,
        bgClass: 'bg-purple-500',
        label: vendor ? `Received an order from ${vendor}` : 'Received an order',
      };
    }

    case 'buyup_engaged':
      return {
        Icon: ShoppingCartIcon,
        bgClass: 'bg-teal-500',
        label: `Buyup work-session for ${resolveVendor(event.payload, vendorNameById)}`,
      };
    case 'exception_logs_engaged':
      return {
        Icon: ExclamationTriangleIcon,
        bgClass: 'bg-orange-500',
        label: 'Used exception logs',
      };
    case 'sales_journal_engaged':
      return { Icon: ChartBarIcon, bgClass: 'bg-orange-500', label: 'Used sales journal' };
    case 'mcp_tool_used':
      // Heatmap-only; filtered out before the list renders. Case present so
      // the exhaustive switch type-checks; never reached in practice.
      return {
        Icon: LinkIcon,
        bgClass: 'bg-blue-400',
        label: `AI assistant called ${event.payload.tool}`,
      };
  }
}

interface EventRowProps {
  event: Event;
  now: Date;
  dayKey: string;
  daySinceSignup: number | null;
  vendorNameById: Map<string, string>;
  highlighted: boolean;
}

function EventRow({
  event,
  now,
  dayKey,
  daySinceSignup,
  vendorNameById,
  highlighted,
}: EventRowProps) {
  const { Icon, bgClass, label, muted } = eventDisplay(event, vendorNameById);
  const createdAt = event.created_at;

  return (
    <li
      data-day={dayKey}
      className={`flex items-center gap-4 rounded-sm py-3 transition ${
        highlighted ? 'ring-2 ring-primary-400 ring-inset' : ''
      }`}
    >
      <span
        aria-hidden="true"
        className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-full text-white ${bgClass}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <p
        className={`min-w-0 flex-1 truncate text-base ${muted ? 'text-gray-500' : 'text-gray-900'}`}
      >
        {label}
      </p>
      <div className="flex flex-none items-center gap-3 text-sm text-gray-500">
        {daySinceSignup !== null && (
          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
            Day {daySinceSignup}
          </span>
        )}
        <time dateTime={createdAt.toISOString()}>{formatAdminTimestamp(createdAt)}</time>
        <span aria-hidden="true" className="text-gray-300">
          ·
        </span>
        <span>{formatDate(createdAt, now).trim()} ago</span>
      </div>
    </li>
  );
}

interface GapBadgeProps {
  days: number;
}

function GapBadge({ days }: GapBadgeProps) {
  return (
    <li className="py-1" aria-hidden="false">
      <p className="text-xs font-medium text-red-700">{days} days with no activity</p>
    </li>
  );
}

export function OnboardingTimelineTab() {
  const { storeId } = onboardingTimelineRoute.useParams();
  const overview = useOnboardingOverview();
  const { events, loading: eventsLoading } = useOnboardingStoreEvents(storeId);
  const { vendors } = useStoreVendors(storeId);
  const integrations = useStoreVendorIntegrations(storeId);

  const [highlightedDay, setHighlightedDay] = useState<string | null>(null);
  const highlightTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (highlightTimeout.current) {
        clearTimeout(highlightTimeout.current);
      }
    },
    []
  );

  if ((overview.isLoading && !overview.data) || eventsLoading) {
    return <Loading />;
  }

  const store = overview.data?.stores.find((s) => s.store.id === storeId);

  if (!store) {
    return (
      <div className="container mx-auto p-4">
        <EmptyState
          icon={<QuestionMarkCircleIcon className="size-8" />}
          title={`Store ${storeId} not found.`}
        />
      </div>
    );
  }

  const now = new Date();
  const vendorNameById = new Map(vendors.map((v) => [v.vendor_id, v.name]));
  const uploadEvents = events.filter((e): e is StoreUploadEvent => e.event_type === 'store_upload');
  // `order_edited` and `mcp_tool_used` events are heatmap-only. `order_edited`
  // is an aggregation emitted at order-close time, so it'd land in the list at
  // the wrong instant; `mcp_tool_used` fires once per AI tool call and would
  // flood the list. Both are still counted in the heatmap.
  const listEvents = events.filter(
    (e) => e.event_type !== 'order_edited' && e.event_type !== 'mcp_tool_used'
  );
  const storeAgeDays = differenceInCalendarDays(now, store.store.created_at);
  const showDayN = storeAgeDays <= DAY_N_VISIBLE_THROUGH_DAYS;

  const handleHeatmapClick = (dateKey: string) => {
    const node = document.querySelector<HTMLElement>(`[data-day="${dateKey}"]`);
    if (!node) {
      return;
    }

    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedDay(dateKey);
    if (highlightTimeout.current) {
      clearTimeout(highlightTimeout.current);
    }

    highlightTimeout.current = setTimeout(() => setHighlightedDay(null), 1000);
  };

  return (
    <div className="container mx-auto space-y-4 p-4">
      <div className="flex items-center gap-2">
        <StorePicker current={store} stores={overview.data?.stores ?? []} />
        <Link
          to="/stores/$storeId"
          params={{ storeId: store.store.id }}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          View Store
          <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
        </Link>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <VendorIntegrationGauge
          vendors={vendors}
          integrations={integrations}
          state={store.store.billing_address.state ?? ''}
        />
        <PricingCoverageGauge uploads={uploadEvents} />
        <NegativeQohGauge uploads={uploadEvents} />
      </div>

      <TimelineHeatmap events={events} now={now} onCellClick={handleHeatmapClick} />

      {listEvents.length === 0 ? (
        <EmptyState icon={<ClockIcon className="size-8" />} title="No events yet for this store." />
      ) : (
        <>
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Timeline</h2>
            <span className="text-sm text-gray-600">{listEvents.length} events</span>
          </div>
          <ul className="list-none divide-y divide-gray-200 p-0">
            {listEvents.map((event, i) => {
              const daySinceSignup = showDayN
                ? Math.max(0, differenceInCalendarDays(event.created_at, store.store.created_at))
                : null;
              const next = listEvents[i + 1];
              const gapDays = next
                ? differenceInCalendarDays(event.created_at, next.created_at)
                : 0;
              const dayKey = formatDateISO(event.created_at);

              return (
                <Fragment key={event.event_id}>
                  <EventRow
                    event={event}
                    now={now}
                    dayKey={dayKey}
                    daySinceSignup={daySinceSignup}
                    vendorNameById={vendorNameById}
                    highlighted={highlightedDay === dayKey}
                  />
                  {gapDays > 7 && <GapBadge days={gapDays} />}
                </Fragment>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
